<template>
  <TourFilterPopupWrapper
    is-fullscreen
    show-close-button
    :title="t('title.header')"
    filter-key="modal"
    :is-disabled-reset-button="isDisabledResetButton"
    :modal-size="modalSize"
    modal-class="modal-filters"
    is-modal-only
  >
    <template #trigger="{ open }">
      <div class="filters-open-btn">
        <SkeletonBlock
          :is-active="isSkeletonActive"
          :width="filterButtonSkeletonWidth"
          :height="48"
        >
          <UiButton
            v-if="!isSkeletonActive"
            :text="t('title.header')"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            :size="currentSize"
            @clicked="open"
          >
            <template #icon><ModalFilterBtnIcon /></template>
          </UiButton>
          <div
            v-if="!isSkeletonActive && filtersCount"
            class="filters-count"
          >
            {{ filtersCount }}
          </div>
        </SkeletonBlock>
      </div>
    </template>

    <div class="filters">
      <TourFilterRow :title="t('title.format')">
        <TourFiltersFormatContent />
      </TourFilterRow>
      <TourFilterRow
        :title="t('title.priceTour', { currencySymbol })"
        class="price-row"
      >
        <TourFilterPriceContent
          v-bind="props.price"
          :is-modal="true"
        />
      </TourFilterRow>

      <TourFilterRow>
        <TourPersonStepper bold-title />
      </TourFilterRow>

      <TourFilterRow
        :title="t('title.duration')"
        class="duration-row"
      >
        <TourFilterDurationContent :max-duration="maxDurationEnd" />
      </TourFilterRow>

      <TourFilterRow :title="t('title.comfort_type')">
        <ComfortCheckboxList
          :filter-items="filtersStore.state.comfort_type"
          @apply="applyComfortFilter"
        />
      </TourFilterRow>

      <TourFilterRow
        class="accommodation-filter"
        :title="t('title.accommodation_type')"
      >
        <p class="filter-description">{{ t('title.accommodation_description') }}</p>
        <AccommodationCheckboxList
          :filter-items="filtersStore.state.accommodation_type"
          @apply="applyAccommodationFilter"
        />
      </TourFilterRow>

      <TourFilterRow :title="t('title.activity_level')">
        <ActivityLevelCheckBoxList
          :filter-items="filtersStore.state.activity_level_category"
          @apply="applyActivityLevelFilter"
        />
      </TourFilterRow>

      <TourFilterRow
        :title="t('title.children')"
        class="children-row"
      >
        <ChildAllowedCheckbox
          v-model="filtersStore.state.childrenAllowed"
          :checked="!!filtersStore.state.childrenAllowed"
        />
      </TourFilterRow>

      <TourFilterRow :title="t('title.sort')">
        <TourSort v-model="filtersStore.state.sorting as string" />
      </TourFilterRow>
    </div>
  </TourFilterPopupWrapper>
</template>

<script setup lang="ts">
  import { TourFilterPopupWrapper } from '@/entities/filter/popup-wrapper';
  import { UiButton, UiButtonColorMode, UiButtonSize, UiButtonVariant } from '@experience/core-button-ui';
  import { ModalFilterBtnIcon } from '@/shared/ui/icons';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { TourFilterDurationContent } from '@/features/filter/duration';
  import { type TTourFiltersState, useTourFiltersStore } from '@/entities/filter/store/tour';
  import { TourFilterPriceContent } from '@/features/filter/price';
  import TourFilterRow from './TourFilterRow.vue';
  import TourSort from './TourTheSort.vue';
  import { DAY_HOURS } from '@/features/filter/duration/const/config.ts';
  import { Sort } from '@/widgets/filtersBlock/types';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { type CurrencyCode, getCurrencySymbol } from '@/shared/lib/currency';
  import { UiModalSize } from '@experience/core-modal-ui';
  import { useScreenSize } from '@experience/shared';
  import TourPersonStepper from '@/features/filter/dates/ui/tour/TourPersonStepper.vue';
  import type { TTourAccommodation, TTourActivityLevel, TTourComfort } from '@/entities/filter/store/tour';
  import { ComfortCheckboxList } from '@/entities/filter/comfort';
  import { AccommodationCheckboxList } from '@/entities/filter/accommodation';
  import { ActivityLevelCheckBoxList } from '@/entities/filter/activity-level';
  import { ChildAllowedCheckbox } from '@/entities/filter/child-allowed';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { TourFiltersFormatContent } from '@/features/filter/format';

  interface Props {
    isScroll: boolean;
    price: {
      histogram: Record<string, number>;
      minPrice: number;
      maxPrice: number;
    };
    durationParams: Record<string, number>;
    currencyCode: CurrencyCode;
  }

  const { isSkeletonActive } = useListingSkeleton();

  const props = defineProps<Props>();

  const maxDurationEnd = computed(() => {
    return Number(Object.keys(props.durationParams).at(-1)) / DAY_HOURS;
  });

  const filtersStore = useTourFiltersStore();

  const { isMobile } = useScreenSize();

  const modalSize = computed(() => (isMobile.value ? UiModalSize.FULLSCREEN : UiModalSize.S));
  const filterButtonSkeletonWidth = computed(() => (isMobile.value ? '100%' : 135));

  const currencySymbol = computed(() => getCurrencySymbol(props.currencyCode));

  const countCortegeFilter = (value: Array<string | number | null>) => {
    return value?.some((item) => item !== null) ? 1 : 0;
  };

  const countBooleanFilter = (value: boolean | null) => {
    return value ? 1 : 0;
  };

  const countArrayFilter = (value: Array<string>) => {
    return value?.length ? 1 : 0;
  };

  function countAppliedFilters(state: TTourFiltersState) {
    let count = 0;

    Object.entries(state).forEach(([key, value]) => {
      switch (key) {
        case 'price':
        case 'duration':
        case 'comfort_type':
        case 'activity_level_category':
        case 'accommodation_type':
          count += countCortegeFilter(value as Array<string | null | number>);
          break;
        case 'date':
          break;
        case 'persons':
          count += (value as number) > 1 ? 1 : 0;
          break;
        case 'tourFormat':
          count += countArrayFilter(value as Array<string>);
          break;
        case 'childrenAllowed':
          count += countBooleanFilter(value as boolean);
          break;
        default:
          count += 0;
      }
    });

    return count;
  }

  const filtersCount = computed(() => {
    return countAppliedFilters(filtersStore.searchState);
  });

  const filtersSelectedCount = computed(() => {
    return countAppliedFilters(filtersStore.state);
  });

  const isDisabledResetButton = computed(() => {
    return (
      filtersSelectedCount.value < 1 && (!filtersStore.state.sorting || filtersStore.state.sorting === Sort.popularity)
    );
  });

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const currentSize = computed(() => {
    if (isMobile.value) {
      return UiButtonSize.M;
    }

    return props.isScroll ? UiButtonSize.S : UiButtonSize.M;
  });

  const applyComfortFilter = (value: TTourComfort) => {
    filtersStore.state.comfort_type = value;
  };

  const applyAccommodationFilter = (value: TTourAccommodation) => {
    filtersStore.state.accommodation_type = value;
  };

  const applyActivityLevelFilter = (value: TTourActivityLevel) => {
    filtersStore.state.activity_level_category = value;
  };
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  .filters {
    display: flex;
    flex-direction: column;
    gap: 40px;

    .price-row,
    .duration-row,
    .children-row {
      gap: 16px;
    }

    .accommodation-filter {
      gap: 4px;
    }
  }

  .filter-description {
    @include typography.base-400-400-regular;
  }

  .filters-open-btn {
    position: relative;
    display: flex;
    align-items: center;
  }

  .filters-count {
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border: 2px solid var(--comp-badge-color-border-default);
    border-radius: var(--alias-radius-rounded);
    background: var(--comp-badge-color-background-neutral);
    color: var(--comp-badge-color-text);
    font-weight: var(--global-typography-weight-regular);
    font-style: normal;
    font-size: 12px;
    line-height: 16px;
    transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
  }

  :deep(.footer-persons) {
    margin-bottom: 0;
  }
</style>
